import type { AnalyticsEvents } from '@sunflower-games/analytics-events';

import { analytics } from '../../services/analytics';
import type { EventTriggersResponse } from '../../stores/event-trigger/types';
import { getBenefitTypeAnalyticsProps } from '../../utils/events';

import type { CategoryBanner, CategoryBannerCtaType } from './types';

const BENEFIT_CTA_TYPES: CategoryBannerCtaType[] = ['OFFER', 'GIFT', 'MESSAGE'];

export function getCategoryBannerAnalyticsProps(
  banner: CategoryBanner,
  eventTriggerResponse: EventTriggersResponse | null | undefined,
) {
  const props = {
    event_parent: 'lobby',
    event_category: 'lobby_banner',
    event_type: 'banner_cta_type',
    event_value: banner.ctaType.toLowerCase() as Lowercase<CategoryBannerCtaType>,
    identifier: banner.id,
    position: banner.position,
    page_url: {},
  } as const;
  const { ctaValue } = banner;
  if (!ctaValue || !BENEFIT_CTA_TYPES.includes(banner.ctaType)) return props;

  const benefitType = [
    ...(eventTriggerResponse?.currentEventsByTrigger ?? []),
    ...(eventTriggerResponse?.otherActiveEvents ?? []),
    ...(eventTriggerResponse?.personalGifts ?? []),
  ].find((event) => (event.benefit?.key ?? event.benefitKey) === ctaValue)?.benefit?.benefitType;

  return {
    ...props,
    benefit_key: ctaValue,
    ...(benefitType && getBenefitTypeAnalyticsProps(benefitType)),
  };
}

// lobby_banner / cta_click aren't keyed in @sunflower-games/analytics-events yet; same cast pattern as
// analytics.sendMiniGameEvent, kept in one place until the package gains them.
export function sendCategoryBannerEvent(
  eventName: 'lobby_banner' | 'cta_click',
  params: ReturnType<typeof getCategoryBannerAnalyticsProps> & {
    event_action: 'impression' | 'click';
    button_text?: string;
  },
) {
  return analytics.sendEvent(
    eventName as keyof AnalyticsEvents,
    params as unknown as AnalyticsEvents[keyof AnalyticsEvents],
  );
}
